import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from '../../../../../../index';
import FormsRow from '../../../../../FormsRow/FormsRow';
import FormInput from '../../../../../FormInput/FormInput';
import { Namespace } from '../../../../../../../../../translation/namespace';

import {
  getContentTopResults,
  AbstractFormsComboBox,
  ListItemPage,
  ListBoxModifier,
  TopContentResult,
  useSingleSelectComboBox,
  ListItemEmpty,
  ContentPreview,
} from '../../../../../ComboBox';
import { StatusInfoError } from '../../../../../../../../../_lib/components/StatusInfo';
import { FormSubmitButton } from '../../../../../FormSubmitButton/FormSubmitButton';
import { LinkchooserLinkType } from '../../../../../../../../../types/api/LinkchooserLinkType';
import { StatusInfoModifier } from '../../../../../../../../../_lib/components/StatusInfo/partials/StatusInfo';

type SelectedResult = TopContentResult & { error?: { status: any } };
export function LinkchooserContentTemplate() {
  const { inputs, onChange, submitActionEnabled } = useFormContext();

  const { name, required, constraints, comboBoxUrl } = inputs.q;

  const { t } = useTranslation(Namespace.Linkchooser);

  const initialItem = inputs.q.selected && TopContentResult(inputs.q.selected);
  const linkType = LinkchooserLinkType.Page;
  const { getComboBoxProps, clearSelection, selectedValue } = useSingleSelectComboBox<SelectedResult, any>(
    getContentTopResults,
    {
      suggestUrl: comboBoxUrl?.url || '',
      inputValue: initialItem?.item,
    },
    initialItem
  );

  useEffect(() => {
    // https://tracker.seitenbau.net/browse/SIBZIEL-13253
    if (!selectedValue) {
      inputs.q.clear(false);
      inputs.text.clear(false);
    } else {
      onChange(name, selectedValue?.id);
      inputs.q.selected = selectedValue;
    }

    if (!inputs.text.value) {
      const text = selectedValue?.title || '';
      onChange(inputs.text.name, text);
    }
  }, [selectedValue]);

  const hasValueChanged = () => {
    return (
      submitActionEnabled() &&
      ((inputs.q.value && inputs.q.value !== '') ||
        (inputs.anchor && inputs.anchor.value !== '') ||
        inputs.text.valueChanged())
    );
  };

  return (
    <>
      <FormsRow>
        <AbstractFormsComboBox
          id="combobox-linkchooser"
          name={name}
          required={required}
          labelTitle={t(`${linkType}.q.label`)}
          labelHelpText={t(`${linkType}.q.labelHelpText`) as string}
          maxLength={constraints?.maxLength}
          modifierListBox={ListBoxModifier.RELATIVE}
          {...getComboBoxProps()}
        >
          {item => (item ? <ListItemPage page={item} showOnlyFormatForFiles /> : <ListItemEmpty />)}
        </AbstractFormsComboBox>
      </FormsRow>
      {selectedValue && selectedValue.error && (
        <StatusInfoError
          modifier={StatusInfoModifier.MARGIN_BOTTOM}
          message={t(`errors.${selectedValue.error.status}`) as string}
        />
      )}
      {selectedValue && selectedValue.pageLink && <ContentPreview {...selectedValue} reset={clearSelection} />}
      {
        // anchors not possible on files
        !inputs.q.selected?.contentType && (
          <FormsRow>
            <FormInput
              id="linkchooser-anchor"
              labelTitle={t(`${linkType}.anchor.label`)}
              name={inputs.anchor.name}
              value={inputs.anchor.value}
              maxLength={inputs.anchor.constraints?.maxLength}
              patternMismatchErrorMessage={inputs.anchor.isValid() ? null : t('anchorPatternMismatchErrorMessage')}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => onChange(target.name, target.value)}
              pattern={inputs.anchor?.constraints?.pattern}
              labelHelpText={t('anchorHelptext')}
            />
          </FormsRow>
        )
      }

      <FormsRow>
        <FormInput
          id="linkchooser-text-input"
          labelTitle={t(`${linkType}.text.label`)}
          name={inputs.text.name}
          value={inputs.text.value}
          maxLength={inputs.text.constraints?.maxLength}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => onChange(inputs.text.name, target.value)}
        />
      </FormsRow>
      <FormSubmitButton buttonLabel={t(`${linkType}.submitFormButtonLabel`)} enabled={hasValueChanged()} />
    </>
  );
}
